import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface LogoBuilderProps {
  initialKeywords?: string;
  initialColors?: string[];
  initialShape?: string;
  onSaveKeywords: (value: string) => void;
  onSaveColors: (value: string[]) => void;
  onSaveShape: (value: string) => void;
}

interface ColorOption {
  name: string;
  hex: string;
}

const COLOR_OPTIONS: ColorOption[] = [
  { name: 'Black', hex: '#000000' },
  { name: 'Gray', hex: '#6B7280' },
  { name: 'Forest Green', hex: '#166534' },
  { name: 'Emerald', hex: '#10B981' },
  { name: 'Yellow', hex: '#EAB308' },
  { name: 'Coral', hex: '#F97066' },
  { name: 'White', hex: '#FFFFFF' },
  { name: 'Teal', hex: '#14B8A6' },
  { name: 'Navy Blue', hex: '#1E3A5F' },
  { name: 'Royal Blue', hex: '#4169E1' },
  { name: 'Purple', hex: '#8B5CF6' },
  { name: 'Magenta', hex: '#D946EF' },
  { name: 'Orange', hex: '#F97316' },
  { name: 'Red', hex: '#EF4444' },
  { name: 'Burgundy', hex: '#7F1D1D' },
  { name: 'Brown', hex: '#92400E' },
  { name: 'Lime', hex: '#84CC16' },
  { name: 'Mint', hex: '#6EE7B7' },
  { name: 'Sky Blue', hex: '#38BDF8' },
  { name: 'Pink', hex: '#F472B6' },
];

const SHAPE_OPTIONS = [
  'Circle',
  'Rounded Square',
  'Square',
  'Triangle',
  'Pentagon',
  'Star',
  'Diamond',
  'Oval',
] as const;

function ShapeSvg({ shape, size = 48, fill = '#E5E7EB' }: { shape: string; size?: number; fill?: string }) {
  const half = size / 2;
  switch (shape) {
    case 'Circle':
      return <circle cx={half} cy={half} r={half - 2} fill={fill} />;
    case 'Rounded Square':
      return <rect x={2} y={2} width={size - 4} height={size - 4} rx={8} fill={fill} />;
    case 'Square':
      return <rect x={2} y={2} width={size - 4} height={size - 4} fill={fill} />;
    case 'Triangle':
      return <polygon points={`${half},4 ${size - 4},${size - 4} 4,${size - 4}`} fill={fill} />;
    case 'Pentagon': {
      const pts = [0, 1, 2, 3, 4].map((i) => {
        const angle = (Math.PI * 2 * i) / 5 - Math.PI / 2;
        return `${half + (half - 3) * Math.cos(angle)},${half + (half - 3) * Math.sin(angle)}`;
      });
      return <polygon points={pts.join(' ')} fill={fill} />;
    }
    case 'Star': {
      const pts: string[] = [];
      for (let i = 0; i < 10; i++) {
        const angle = (Math.PI * 2 * i) / 10 - Math.PI / 2;
        const r = i % 2 === 0 ? half - 3 : (half - 3) * 0.45;
        pts.push(`${half + r * Math.cos(angle)},${half + r * Math.sin(angle)}`);
      }
      return <polygon points={pts.join(' ')} fill={fill} />;
    }
    case 'Diamond':
      return (
        <polygon
          points={`${half},4 ${size - 4},${half} ${half},${size - 4} 4,${half}`}
          fill={fill}
        />
      );
    case 'Oval':
      return <ellipse cx={half} cy={half} rx={half - 2} ry={half * 0.65} fill={fill} />;
    default:
      return <circle cx={half} cy={half} r={half - 2} fill={fill} />;
  }
}

export default function LogoBuilder({
  initialKeywords = '',
  initialColors = [],
  initialShape = '',
  onSaveKeywords,
  onSaveColors,
  onSaveShape,
}: LogoBuilderProps) {
  const [keywords, setKeywords] = useState(initialKeywords);
  const [selectedColors, setSelectedColors] = useState<string[]>(initialColors);
  const [selectedShape, setSelectedShape] = useState(initialShape);

  useEffect(() => {
    setKeywords(initialKeywords);
  }, [initialKeywords]);

  useEffect(() => {
    setSelectedColors(initialColors);
  }, [initialColors]);

  useEffect(() => {
    setSelectedShape(initialShape);
  }, [initialShape]);

  const toggleColor = (colorName: string) => {
    let next: string[];
    if (selectedColors.includes(colorName)) {
      next = selectedColors.filter((c) => c !== colorName);
    } else if (selectedColors.length < 2) {
      next = [...selectedColors, colorName];
    } else {
      // Replace the first selected color
      next = [selectedColors[1], colorName];
    }
    setSelectedColors(next);
    onSaveColors(next);
  };

  const selectShape = (shape: string) => {
    setSelectedShape(shape);
    onSaveShape(shape);
  };

  const getGradientColors = (): [string, string] => {
    const c1 = COLOR_OPTIONS.find((c) => c.name === selectedColors[0])?.hex ?? '#E5E7EB';
    const c2 = COLOR_OPTIONS.find((c) => c.name === selectedColors[1])?.hex ?? c1;
    return [c1, c2];
  };

  const [gc1, gc2] = getGradientColors();

  return (
    <div className="mx-auto max-w-md">
      <h2 className="text-center text-2xl font-bold text-gray-900">Logo Builder</h2>
      <p className="mt-1 text-center text-sm text-gray-500">Design your business logo (optional)</p>

      {/* Keywords */}
      <div className="mt-6">
        <label className="text-sm font-medium text-gray-700">Keywords</label>
        <input
          type="text"
          value={keywords}
          onChange={(e) => {
            setKeywords(e.target.value);
            onSaveKeywords(e.target.value);
          }}
          placeholder="friendly natural 🌿"
          className="mt-1 w-full rounded-xl border-2 border-gray-200 px-4 py-2.5 text-gray-900 placeholder-gray-300 focus:border-brand-500 focus:outline-none"
        />
      </div>

      {/* Colors */}
      <div className="mt-6">
        <label className="text-sm font-medium text-gray-700">
          Colors <span className="text-gray-400">(pick up to 2)</span>
        </label>
        <div className="mt-2 grid grid-cols-5 gap-2">
          {COLOR_OPTIONS.map((color) => {
            const isSelected = selectedColors.includes(color.name);
            return (
              <motion.button
                key={color.name}
                whileTap={{ scale: 0.9 }}
                onClick={() => toggleColor(color.name)}
                title={color.name}
                className={`relative flex h-10 w-full items-center justify-center rounded-lg border-2 transition-all ${
                  isSelected
                    ? 'border-brand-500 shadow-md'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                style={{ backgroundColor: color.hex }}
              >
                {isSelected && (
                  <span className={`text-sm font-bold ${
                    ['White', 'Yellow', 'Mint', 'Lime'].includes(color.name)
                      ? 'text-gray-800'
                      : 'text-white'
                  }`}>
                    ✓
                  </span>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Shapes */}
      <div className="mt-6">
        <label className="text-sm font-medium text-gray-700">
          Shape <span className="text-gray-400">(pick 1)</span>
        </label>
        <div className="mt-2 grid grid-cols-4 gap-2">
          {SHAPE_OPTIONS.map((shape) => (
            <motion.button
              key={shape}
              whileTap={{ scale: 0.9 }}
              onClick={() => selectShape(shape)}
              className={`flex flex-col items-center rounded-xl border-2 px-2 py-3 transition-all ${
                selectedShape === shape
                  ? 'border-brand-500 bg-brand-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <svg width={40} height={40} viewBox="0 0 48 48">
                <ShapeSvg shape={shape} fill="#9CA3AF" />
              </svg>
              <span className="mt-1 text-xs text-gray-600">{shape}</span>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Live preview */}
      {(selectedShape || selectedColors.length > 0) && (
        <div className="mt-6">
          <label className="text-sm font-medium text-gray-700">Preview</label>
          <div className="mt-2 flex items-center justify-center rounded-xl border-2 border-gray-200 bg-gray-50 py-6">
            <svg width={96} height={96} viewBox="0 0 48 48">
              <defs>
                <linearGradient id="logoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={gc1} />
                  <stop offset="100%" stopColor={gc2} />
                </linearGradient>
              </defs>
              <ShapeSvg
                shape={selectedShape || 'Circle'}
                fill="url(#logoGrad)"
              />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
}
